import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CurrentSubscription } from "@/components/subscription/CurrentSubscription";
import { SubscriptionPlans } from "@/components/subscription/SubscriptionPlans";
import { CreditPackages } from "@/components/subscription/CreditPackages";

interface Subscription {
  tier: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  payment_method_type: string | null;
  payment_method_last4: string | null;
}

const Subscription = () => {
  const { session } = useAuth();
  const { toast } = useToast();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        if (!session?.user?.id) {
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .single();

        if (error) throw error;
        setSubscription(data);
      } catch (error) {
        console.error('Error fetching subscription:', error);
        toast({
          variant: "destructive",
          title: "Error fetching subscription",
          description: "Please try again later",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [session?.user?.id, toast]);

  const handleUpgrade = async (newTier: string) => {
    toast({
      title: "Coming soon",
      description: "Subscription upgrades will be available soon",
    });
  };

  const handleBuyCredits = async (amount: number) => {
    toast({
      title: "Coming soon",
      description: "Credit purchases will be available soon",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <main className="flex-1 container py-6">
          <div className="max-w-4xl mx-auto">
            Loading...
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 container py-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Subscription Management</h1>
            <p className="text-muted-foreground">Manage your subscription and billing details</p>
          </div>

          <CurrentSubscription subscription={subscription} />
          <SubscriptionPlans currentTier={subscription?.tier} onUpgrade={handleUpgrade} />
          <CreditPackages onBuyCredits={handleBuyCredits} />
        </div>
      </main>
    </div>
  );
};

export default Subscription;